import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SearchAllTypesDto } from '@gov-nx/api';
import {
	searchAllTypesQuery,
	useCmsRecommendedLinksQuery,
	useAxiosCmsInstance,
	usePoQuery,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useCmsEvents } from '@gov-nx/core/events';
import {
	useAppLanguage,
	useSearchRecentVisited,
	useSearchTermsStorage,
} from '@gov-nx/core/hooks';
import type { Nullable, Optional } from '@gov-nx/core/types';
import { isNumber } from '@gov-nx/core/types';
import { useAuthStore, useUserStoreFavorites } from '@gov-nx/store';
import {
	sanitizeHTMLTags,
	useBoolean,
	useDebounce,
	useFilter,
} from '@gov-nx/utils/common';
import { pageRouteResolver } from '@gov-nx/web';
import { PageCode } from '../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type {
	SearchAllRecentVisited,
	SearchAllTypesContext,
	SearchAllTypesListFilter,
	SearchAllTypesResult,
} from './context.types';
import { SearchAllResultType } from './context.types';
import {
	resolveLinkFromCmsSignpost,
	resolveLinkFromSearchAllTypes,
	searchResultsComparator,
	isSearchValid,
} from './utils';

const HledaniContext = createContext<Nullable<SearchAllTypesContext>>(null);

const getRecordType = (item: SearchAllTypesDto): SearchAllResultType => {
	if (item.usefulLink) {
		return SearchAllResultType.UsefulLink;
	} else if (item.faq) {
		return SearchAllResultType.Faq;
	}
	return SearchAllResultType.Signpost;
};

export function HledaniContextProvider({ children }: PropsWithChildren) {
	const favorites = useUserStoreFavorites();
	const {
		extraConfig: { environment, application },
	} = useExtraConfigContext();
	const { language } = useAppLanguage();
	const activeReza = useAuthStore('activeReza');
	const activeRezaDesignation = useAuthStore('activeRezaDesignation');
	const searchTerm = useDebounce<string>(200);
	const { axiosCmsBaseInstance } = useAxiosCmsInstance();
	const isSortedAlphabetically = useBoolean();
	const isSearchOpened = useBoolean(false);
	const { cmsExternalServiceModal } = useCmsEvents();
	const [currentSearchValue, setCurrentSearchValue] = useState<
		Nullable<string> | undefined
	>('');
	const { addSearchTerm, removeSearchTerm, searchTerms } =
		useSearchTermsStorage();
	const { addRecentVisited, recentVisited } = useSearchRecentVisited();

	const navigate = useNavigate();

	const { filter, setPartialFilter } = useFilter<SearchAllTypesListFilter>({
		key: 'search-all-types',
		initialState: {
			language,
			type: null,
			favourites: false,
			pageSize: 10,
		},
	});
	const {
		filter: recommendedFilter,
		setPartialFilter: setRecommdedPartialFilter,
	} = useFilter<{ pageSize: number }>({
		key: 'recommended-links',
		initialState: {
			pageSize: 10,
		},
	});

	const { data, isFetching } = usePoQuery<Optional<SearchAllTypesDto[]>>({
		queryKey: [
			'search-all-types',
			filter,
			searchTerm.value,
			activeRezaDesignation,
		],
		queryFn: async (): Promise<SearchAllTypesDto[]> => {
			if (!searchTerm.value?.length) {
				return [];
			}

			const params = prepareSubmitData(
				filter,
				searchTerm.value,
				application,
				environment,
				activeRezaDesignation ?? undefined
			);
			const data = await searchAllTypesQuery(axiosCmsBaseInstance, params);
			return data.data ?? [];
		},
	});

	const { data: recommendedLinksData } = useCmsRecommendedLinksQuery();

	const resolveAction = async (item: SearchAllRecentVisited) => {
		await addRecentVisited(item);

		if (
			item.type === SearchAllResultType.Signpost &&
			item.actionParams.externalServiceId
		) {
			return cmsExternalServiceModal({
				id: item.actionParams.externalServiceId,
			});
		}
	};

	const resultsInTab: SearchAllTypesResult[] = (
		searchTerm.value ? (data ?? []) : []
	)
		.filter((item) => {
			const type = getRecordType(item);

			if (filter.type === null) {
				return true;
			}
			return type === filter.type;
		})
		.map((item) => ({
			title: item.highlightName,
			titleForSorting:
				item.signpost?.name ??
				item.faq?.question ??
				item.usefulLink?.title ??
				'',
			description: item.highlightDescription,
			type: getRecordType(item),
			code:
				item.signpost?.pageCode ||
				item.signpost?.serviceCode ||
				item.signpost?.externalServiceCode ||
				undefined,
			link: resolveLinkFromSearchAllTypes(item),
			action: async () => {
				isSearchOpened.setFalse();

				const title = sanitizeHTMLTags(item.highlightName);
				const activeRezaKod = activeReza?.kod ?? '';

				if (item.signpost) {
					const { pageCode, serviceCode, externalServiceId } = item.signpost;

					if (pageCode) {
						await resolveAction({
							title,
							rezaKod: activeRezaKod,
							type: SearchAllResultType.Signpost,
							actionParams: { pageCode },
						});
					} else if (serviceCode) {
						await resolveAction({
							title,
							rezaKod: activeRezaKod,
							type: SearchAllResultType.Signpost,
							actionParams: { serviceCode },
						});
					} else if (isNumber(externalServiceId)) {
						await resolveAction({
							title,
							rezaKod: activeRezaKod,
							type: SearchAllResultType.Signpost,
							actionParams: { externalServiceId },
						});
					}
				} else if (item.usefulLink) {
					await resolveAction({
						title,
						rezaKod: activeRezaKod,
						type: SearchAllResultType.UsefulLink,
						actionParams: { url: item.usefulLink.link },
					});
				} else if (item.faq) {
					await resolveAction({
						title,
						rezaKod: activeRezaKod,
						type: SearchAllResultType.Faq,
						actionParams: { faqSlug: item.faq.slug },
					});
				}
			},
			order: item.signpost?.order ?? 0,
		}))
		.sort(searchResultsComparator(isSortedAlphabetically.value));

	const results = resultsInTab.slice(0, filter.pageSize);

	const resultsRecommededLinks: SearchAllTypesResult[] = (
		recommendedLinksData ?? []
	).map((item) => ({
		action: async () => {
			isSearchOpened.setFalse();

			if (!item.signpost) {
				return;
			}

			const title = item.signpost.name;
			const activeRezaKod = activeReza?.kod ?? '';

			const { pageCode, serviceCode, externalServiceId } = item.signpost;

			if (pageCode) {
				await resolveAction({
					title,
					rezaKod: activeRezaKod,
					type: SearchAllResultType.Signpost,
					actionParams: { pageCode },
				});
			} else if (serviceCode) {
				await resolveAction({
					title,
					rezaKod: activeRezaKod,
					type: SearchAllResultType.Signpost,
					actionParams: { serviceCode },
				});
			} else if (isNumber(externalServiceId)) {
				await resolveAction({
					title,
					rezaKod: activeRezaKod,
					type: SearchAllResultType.Signpost,
					actionParams: { externalServiceId },
				});
			}
		},
		link: resolveLinkFromCmsSignpost(item.signpost),
		description: item.signpost.description,
		code:
			item.signpost?.pageCode ||
			item.signpost?.serviceCode ||
			item.signpost?.externalServiceCode ||
			undefined,
		titleForSorting: item.signpost.name,
		type: SearchAllResultType.Signpost,
		title: item.signpost.name,
		order: item.signpost.order,
	}));

	const unfocusAll = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
	};

	const getSearchResultsPageUrl = (q?: string): string =>
		pageRouteResolver(PageCode['vysledky-vyhledavani'], { q });

	const navigateToSearchResultsPage = (searchTerm?: string) =>
		navigate(getSearchResultsPageUrl(searchTerm));

	const onSearch = () => {
		unfocusAll();
		isSearchOpened.setFalse();
	};

	const formDefinition = FormInstance();
	const searchValue = formDefinition.formMethods.getValues('search');
	const { isSearchValue } = isSearchValid(searchValue);

	const onSubmit = formDefinition.formMethods.handleSubmit(async () => {
		const value = formDefinition.formMethods.getValues('search');
		if (!value) {
			return;
		}
		onSearch();
		await addSearchTerm(value);
		navigateToSearchResultsPage(value);
	});

	const setFilterTab = (type: SearchAllResultType) => {
		setPartialFilter({ type, pageSize: 10 });
		setRecommdedPartialFilter({ pageSize: 10 });
	};
	const setFilterAll = () => {
		setPartialFilter({ type: null, pageSize: 10 });
		setRecommdedPartialFilter({ pageSize: 10 });
	};

	const numberOfResults = resultsInTab.length;

	useEffect(() => {
		const subscription = formDefinition.formMethods.watch((value) => {
			const searchValue = value.search;
			setCurrentSearchValue(searchValue);
			const { isSearchValue, validSearchValue } = isSearchValid(searchValue);

			if (isSearchValue) {
				setPartialFilter({ type: null, favourites: false, pageSize: 10 });
				setRecommdedPartialFilter({ pageSize: 10 });
				searchTerm.update(validSearchValue);
			} else {
				searchTerm.reset();
			}
		});
		return () => subscription.unsubscribe();
	}, [
		formDefinition.formMethods,
		searchTerm,
		setPartialFilter,
		setRecommdedPartialFilter,
	]);

	const recommendedResults = resultsRecommededLinks.slice(
		0,
		recommendedFilter.pageSize
	);

	const canLoadMoreRecommended =
		resultsRecommededLinks.length > recommendedFilter.pageSize;

	const loadMoreRecommended = () => {
		setRecommdedPartialFilter({ pageSize: recommendedFilter.pageSize + 10 });
	};

	const loadMoreResults = () => {
		setPartialFilter({
			pageSize: filter.pageSize + 10,
		});
	};

	return (
		<HledaniContext.Provider
			value={{
				setFilterTab,
				setFilterAll,
				numberOfResults,
				filter,
				results,
				isFetching,
				formDefinition,
				onSubmit,
				loadMoreResults,
				favorites,
				searchTerm,
				isSortedAlphabetically,
				searchTerms,
				searchValue,
				isSearchValue,
				recommendedResults,
				canLoadMoreRecommended,
				loadMoreRecommended,
				isSearchOpened,
				removeSearchTerm,
				getSearchResultsPageUrl,
				onSearch,
				currentSearchValue,
				recentVisited,
				resolveAction,
			}}>
			{children}
		</HledaniContext.Provider>
	);
}

export const useHledaniContext = (): SearchAllTypesContext => {
	const context = useContext(HledaniContext);
	if (!context) {
		throw new Error(
			'useHledaniContext must be used within a HledaniContextProvider'
		);
	}
	return context;
};
